import { createContext, useContext } from 'react';
import { User, UserRole, UserPermissions, Admin, AdminPermissions } from '@/types';
import { User as FirebaseUser } from 'firebase/auth';

export interface AuthContextType {
    // Current user
    currentUser: FirebaseUser | null;
    userProfile: User | null;
    user: User | null; // alias for userProfile for compatibility
    userRole: UserRole | null;
    permissions: UserPermissions | null;
    firmId: string | null;
    effectiveFirmId: string | null; // Returns impersonatedFirmId when impersonating, otherwise firmId

    // Admin state
    isAdmin: boolean;
    adminProfile: Admin | null;
    adminPermissions: AdminPermissions | null;

    // Impersonation state
    isImpersonating: boolean;
    impersonatedFirmId: string | null;
    impersonatedFirmName: string | null;
    impersonationSessionId: string | null;

    // Loading states
    loading: boolean;
    initializing: boolean;

    // Auth actions
    signup: (email: string, password: string, displayName: string, firmName: string, role: UserRole, salutation?: string, firstName?: string, lastName?: string) => Promise<void>;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    sendVerificationEmail: () => Promise<void>;
    updateUserProfile: (displayName: string) => Promise<void>;
    refreshUserProfile: (forceRefresh?: boolean) => Promise<void>;
    refreshSession: () => Promise<boolean>;

    // Admin actions
    startImpersonation: (firmId: string) => Promise<void>;
    stopImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the useAuth hook from the definition file to avoid react-refresh/only-export-components warning
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
